import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Users,
  User,
  Plus,
  Trash2,
  Edit2,
  HelpCircle,
  Target,
  Briefcase,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import Button from '../ui/Button';
import Card, { CardHeader, CardBody } from '../ui/Card';
import {
  PersonaCreate,
  PersonaResponse,
  StrategicQuestionCreate,
  StrategicQuestionResponse,
} from '../../types/threePhase';
import { standaloneCompaniesAPI } from '../../lib/api';

// Validation schemas
const personaSchema = z.object({
  role: z.string().min(2, 'Role must be at least 2 characters'),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  department: z.string().min(2, 'Department is required'),
  responsibilities: z.array(z.string()).min(1, 'Add at least one responsibility'),
  painPoints: z.array(z.string()).optional(),
  successMetrics: z.array(z.string()).optional(),
});

const questionSchema = z.object({
  questionText: z.string().min(5, 'Question must be at least 5 characters'),
  category: z.string().min(2, 'Category is required'),
  priority: z.enum(['High', 'Medium', 'Low'], {
    errorMap: () => ({ message: 'Select a valid priority level' }),
  }),
  expectedInsight: z.string().optional(),
  dataRequirements: z.array(z.string()).optional(),
});

interface PersonaManagerProps {
  caseId: string;
}

const PersonaManager: React.FC<PersonaManagerProps> = ({ caseId }) => {
  const queryClient = useQueryClient();
  const [selectedPersona, setSelectedPersona] = useState<PersonaResponse | null>(null);
  const [editingPersona, setEditingPersona] = useState<PersonaResponse | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<StrategicQuestionResponse | null>(null);

  // State for dynamic arrays
  const [responsibilityInput, setResponsibilityInput] = useState('');
  const [painPointInput, setPainPointInput] = useState('');
  const [successMetricInput, setSuccessMetricInput] = useState('');
  const [dataReqInput, setDataReqInput] = useState('');

  // ===== PERSONAS =====
  const {
    register: registerPersona,
    handleSubmit: handleSubmitPersona,
    formState: { errors: personaErrors },
    reset: resetPersona,
    setValue: setPersonaValue,
    watch: watchPersona,
  } = useForm<PersonaCreate>({
    resolver: zodResolver(personaSchema),
    defaultValues: {
      role: '',
      name: '',
      department: '',
      responsibilities: [],
      painPoints: [],
      successMetrics: [],
    },
  });

  const responsibilities = watchPersona('responsibilities') || [];
  const painPoints = watchPersona('painPoints') || [];
  const successMetrics = watchPersona('successMetrics') || [];

  const { data: personas } = useQuery({
    queryKey: ['personas', caseId],
    queryFn: async () => {
      const response = await standaloneCompaniesAPI.getAllPersonas(caseId);
      return response.data as PersonaResponse[];
    },
    enabled: !!caseId,
  });

  const createPersonaMutation = useMutation({
    mutationFn: (data: PersonaCreate) => standaloneCompaniesAPI.createPersona(caseId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['personas', caseId] });
      toast.success('Persona added successfully');
      resetPersona();
      // Auto-select newly created persona
      if (response.data) {
        setSelectedPersona(response.data as PersonaResponse);
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add persona');
    },
  });

  const updatePersonaMutation = useMutation({
    mutationFn: ({ personaId, data }: { personaId: string; data: PersonaCreate }) =>
      standaloneCompaniesAPI.updatePersona(caseId, personaId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personas', caseId] });
      toast.success('Persona updated successfully');
      resetPersona();
      setEditingPersona(null);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update persona');
    },
  });

  const deletePersonaMutation = useMutation({
    mutationFn: (personaId: string) => standaloneCompaniesAPI.deletePersona(caseId, personaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personas', caseId] });
      queryClient.invalidateQueries({ queryKey: ['questions', caseId] });
      toast.success('Persona deleted successfully');
      if (selectedPersona) {
        setSelectedPersona(null);
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete persona');
    },
  });

  const handlePersonaSubmit = (data: PersonaCreate) => {
    if (editingPersona) {
      updatePersonaMutation.mutate({ personaId: editingPersona.personaId, data });
    } else {
      createPersonaMutation.mutate(data);
    }
  };

  const handleEditPersona = (persona: PersonaResponse) => {
    setEditingPersona(persona);
    setPersonaValue('role', persona.role);
    setPersonaValue('name', persona.name);
    setPersonaValue('department', persona.department);
    setPersonaValue('responsibilities', persona.responsibilities);
    setPersonaValue('painPoints', persona.painPoints || []);
    setPersonaValue('successMetrics', persona.successMetrics || []);
  };

  const handleDeletePersona = (personaId: string) => {
    if (confirm('Are you sure you want to delete this persona? All associated questions will also be deleted.')) {
      deletePersonaMutation.mutate(personaId);
    }
  };

  const addResponsibility = () => {
    if (responsibilityInput.trim()) {
      setPersonaValue('responsibilities', [...responsibilities, responsibilityInput.trim()]);
      setResponsibilityInput('');
    }
  };

  const removeResponsibility = (index: number) => {
    setPersonaValue('responsibilities', responsibilities.filter((_, i) => i !== index));
  };

  const addPainPoint = () => {
    if (painPointInput.trim()) {
      setPersonaValue('painPoints', [...painPoints, painPointInput.trim()]);
      setPainPointInput('');
    }
  };

  const removePainPoint = (index: number) => {
    setPersonaValue('painPoints', painPoints.filter((_, i) => i !== index));
  };

  const addSuccessMetric = () => {
    if (successMetricInput.trim()) {
      setPersonaValue('successMetrics', [...successMetrics, successMetricInput.trim()]);
      setSuccessMetricInput('');
    }
  };

  const removeSuccessMetric = (index: number) => {
    setPersonaValue('successMetrics', successMetrics.filter((_, i) => i !== index));
  };

  // ===== STRATEGIC QUESTIONS =====
  const {
    register: registerQuestion,
    handleSubmit: handleSubmitQuestion,
    formState: { errors: questionErrors },
    reset: resetQuestion,
    setValue: setQuestionValue,
    watch: watchQuestion,
  } = useForm<StrategicQuestionCreate>({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      questionText: '',
      category: '',
      priority: 'Medium',
      expectedInsight: '',
      dataRequirements: [],
    },
  });

  const dataRequirements = watchQuestion('dataRequirements') || [];

  const { data: questions } = useQuery({
    queryKey: ['questions', caseId, selectedPersona?.personaId],
    queryFn: async () => {
      if (!selectedPersona) return [];
      const response = await standaloneCompaniesAPI.getAllStrategicQuestions(caseId, selectedPersona.personaId);
      return response.data as StrategicQuestionResponse[];
    },
    enabled: !!caseId && !!selectedPersona,
  });

  const createQuestionMutation = useMutation({
    mutationFn: (data: StrategicQuestionCreate & { personaId: string }) =>
      standaloneCompaniesAPI.createStrategicQuestion(caseId, data.personaId, {
        questionText: data.questionText,
        category: data.category,
        priority: data.priority,
        expectedInsight: data.expectedInsight,
        dataRequirements: data.dataRequirements,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', caseId] });
      toast.success('Strategic question added successfully');
      resetQuestion();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add question');
    },
  });

  const updateQuestionMutation = useMutation({
    mutationFn: ({ questionId, data }: { questionId: string; data: StrategicQuestionCreate }) =>
      standaloneCompaniesAPI.updateStrategicQuestion(caseId, selectedPersona?.personaId || '', questionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', caseId] });
      toast.success('Question updated successfully');
      resetQuestion();
      setEditingQuestion(null);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update question');
    },
  });

  const deleteQuestionMutation = useMutation({
    mutationFn: (questionId: string) => standaloneCompaniesAPI.deleteStrategicQuestion(caseId, selectedPersona?.personaId || '', questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions', caseId] });
      toast.success('Question deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete question');
    },
  });

  const handleQuestionSubmit = (data: StrategicQuestionCreate) => {
    if (!selectedPersona) {
      toast.error('Please select a persona first');
      return;
    }

    if (editingQuestion) {
      updateQuestionMutation.mutate({ questionId: editingQuestion.questionId, data });
    } else {
      createQuestionMutation.mutate({ ...data, personaId: selectedPersona.personaId });
    }
  };

  const handleEditQuestion = (question: StrategicQuestionResponse) => {
    setEditingQuestion(question);
    setQuestionValue('questionText', question.questionText);
    setQuestionValue('category', question.category);
    setQuestionValue('priority', question.priority);
    setQuestionValue('expectedInsight', question.expectedInsight);
    setQuestionValue('dataRequirements', question.dataRequirements || []);
  };

  const handleDeleteQuestion = (questionId: string) => {
    if (confirm('Are you sure you want to delete this question?')) {
      deleteQuestionMutation.mutate(questionId);
    }
  };

  const addDataRequirement = () => {
    if (dataReqInput.trim()) {
      setQuestionValue('dataRequirements', [...dataRequirements, dataReqInput.trim()]);
      setDataReqInput('');
    }
  };

  const removeDataRequirement = (index: number) => {
    setQuestionValue('dataRequirements', dataRequirements.filter((_, i) => i !== index));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'text-red-700 bg-red-50';
      case 'Medium': return 'text-yellow-700 bg-yellow-50';
      case 'Low': return 'text-green-700 bg-green-50';
      default: return 'text-neutral-700 bg-neutral-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-6 w-6 text-primary-500" />
          <h2 className="text-2xl font-bold text-neutral-900">Persona Management</h2>
        </div>
        {selectedPersona && (
          <span className="text-sm text-neutral-600">
            Selected: <strong>{selectedPersona.role}</strong>
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* LEFT PANEL: Personas List */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-primary-500" />
                  <h3 className="text-lg font-semibold">Personas ({personas?.length || 0})</h3>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {personas && personas.length > 0 ? (
                <div className="space-y-2">
                  {personas.map((persona) => (
                    <div
                      key={persona.personaId}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedPersona?.personaId === persona.personaId
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-neutral-200 hover:border-primary-300 hover:bg-neutral-50'
                      }`}
                      onClick={() => setSelectedPersona(persona)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-neutral-600" />
                            <h4 className="font-semibold text-neutral-900">{persona.role}</h4>
                          </div>
                          <p className="text-sm text-neutral-600 mt-1">{persona.name}</p>
                          <p className="text-xs text-neutral-500 mt-1">{persona.department}</p>
                          <div className="flex gap-2 mt-2 text-xs text-neutral-600">
                            <span>{(persona.responsibilities ?? []).length} responsibilities</span>
                            {persona.painPoints && persona.painPoints.length > 0 && (
                              <span>• {persona.painPoints.length} pain points</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditPersona(persona);
                            }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePersona(persona.personaId);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-500 text-center py-8">
                  No personas yet. Add your first persona below.
                </p>
              )}
            </CardBody>
          </Card>

          {/* Add/Edit Persona Form */}
          <form onSubmit={handleSubmitPersona(handlePersonaSubmit)} className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-primary-500" />
                  <h3 className="text-lg font-semibold">
                    {editingPersona ? 'Edit Persona' : 'Add Persona'}
                  </h3>
                </div>
              </CardHeader>
              <CardBody className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Role"
                    placeholder="e.g., Chief Executive Officer"
                    {...registerPersona('role')}
                    error={personaErrors.role?.message}
                    required
                  />
                  <Input
                    label="Name"
                    placeholder="e.g., John Smith"
                    {...registerPersona('name')}
                    error={personaErrors.name?.message}
                    required
                  />
                </div>

                <Input
                  label="Department"
                  placeholder="e.g., Executive Leadership"
                  {...registerPersona('department')}
                  error={personaErrors.department?.message}
                  required
                />

                {/* Responsibilities */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-neutral-700">
                    Responsibilities <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add responsibility..."
                      value={responsibilityInput}
                      onChange={(e) => setResponsibilityInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addResponsibility())}
                    />
                    <Button type="button" onClick={addResponsibility} variant="secondary" size="sm">
                      Add
                    </Button>
                  </div>
                  {personaErrors.responsibilities && (
                    <p className="text-sm text-red-500">{personaErrors.responsibilities.message}</p>
                  )}
                  {responsibilities.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {responsibilities.map((item, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm"
                        >
                          {item}
                          <button
                            type="button"
                            onClick={() => removeResponsibility(index)}
                            className="text-primary-500 hover:text-primary-700"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pain Points */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-neutral-700">Pain Points (Optional)</label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add pain point..."
                      value={painPointInput}
                      onChange={(e) => setPainPointInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPainPoint())}
                    />
                    <Button type="button" onClick={addPainPoint} variant="secondary" size="sm">
                      Add
                    </Button>
                  </div>
                  {painPoints.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {painPoints.map((item, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-sm"
                        >
                          {item}
                          <button
                            type="button"
                            onClick={() => removePainPoint(index)}
                            className="text-orange-500 hover:text-orange-700"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Success Metrics */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-neutral-700">Success Metrics (Optional)</label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add success metric..."
                      value={successMetricInput}
                      onChange={(e) => setSuccessMetricInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSuccessMetric())}
                    />
                    <Button type="button" onClick={addSuccessMetric} variant="secondary" size="sm">
                      Add
                    </Button>
                  </div>
                  {successMetrics.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {successMetrics.map((item, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm"
                        >
                          {item}
                          <button
                            type="button"
                            onClick={() => removeSuccessMetric(index)}
                            className="text-green-500 hover:text-green-700"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>

            <div className="flex justify-end gap-3">
              {editingPersona && (
                <Button type="button" variant="outline" onClick={() => {
                  setEditingPersona(null);
                  resetPersona();
                }}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                loading={createPersonaMutation.isPending || updatePersonaMutation.isPending}
              >
                {editingPersona ? 'Update Persona' : 'Add Persona'}
              </Button>
            </div>
          </form>
        </div>

        {/* RIGHT PANEL: Strategic Questions */}
        <div className="space-y-4">
          {selectedPersona ? (
            <>
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="h-5 w-5 text-secondary-500" />
                      <h3 className="text-lg font-semibold">
                        Strategic Questions ({questions?.length || 0})
                      </h3>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  {questions && questions.length > 0 ? (
                    <div className="space-y-3">
                      {questions.map((question) => (
                        <div
                          key={question.questionId}
                          className="p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(question.priority)}`}>
                                  {question.priority}
                                </span>
                                <span className="text-xs text-neutral-500">{question.category}</span>
                              </div>
                              <p className="font-medium text-neutral-900">{question.questionText}</p>
                              {question.expectedInsight && (
                                <p className="text-sm text-neutral-600 mt-2">
                                  <strong>Expected Insight:</strong> {question.expectedInsight}
                                </p>
                              )}
                              {question.dataRequirements && question.dataRequirements.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {question.dataRequirements.map((req, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs"
                                    >
                                      {req}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditQuestion(question)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteQuestion(question.questionId)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-neutral-500 text-center py-8">
                      No questions yet. Add strategic questions for {selectedPersona.role} below.
                    </p>
                  )}
                </CardBody>
              </Card>

              {/* Add/Edit Question Form */}
              <form onSubmit={handleSubmitQuestion(handleQuestionSubmit)} className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Plus className="h-5 w-5 text-secondary-500" />
                      <h3 className="text-lg font-semibold">
                        {editingQuestion ? 'Edit Question' : 'Add Strategic Question'}
                      </h3>
                    </div>
                  </CardHeader>
                  <CardBody className="space-y-4">
                    <Textarea
                      label="Question"
                      placeholder="What strategic question needs to be answered?"
                      rows={3}
                      {...registerQuestion('questionText')}
                      error={questionErrors.questionText?.message}
                      required
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Category"
                        placeholder="e.g., Growth, Efficiency, Risk"
                        {...registerQuestion('category')}
                        error={questionErrors.category?.message}
                        required
                      />

                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Priority <span className="text-red-500">*</span>
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          {...registerQuestion('priority')}
                        >
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                        {questionErrors.priority && (
                          <p className="text-sm text-red-500 mt-1">{questionErrors.priority.message}</p>
                        )}
                      </div>
                    </div>

                    <Textarea
                      label="Expected Insight"
                      placeholder="What insights or outcomes are expected from this question?"
                      rows={2}
                      {...registerQuestion('expectedInsight')}
                      error={questionErrors.expectedInsight?.message}
                    />

                    {/* Data Requirements */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-neutral-700">
                        Data Requirements (Optional)
                      </label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Add data requirement..."
                          value={dataReqInput}
                          onChange={(e) => setDataReqInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDataRequirement())}
                        />
                        <Button type="button" onClick={addDataRequirement} variant="secondary" size="sm">
                          Add
                        </Button>
                      </div>
                      {dataRequirements.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {dataRequirements.map((item, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                            >
                              {item}
                              <button
                                type="button"
                                onClick={() => removeDataRequirement(index)}
                                className="text-blue-500 hover:text-blue-700"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>

                <div className="flex justify-end gap-3">
                  {editingQuestion && (
                    <Button type="button" variant="outline" onClick={() => {
                      setEditingQuestion(null);
                      resetQuestion();
                    }}>
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="submit"
                    loading={createQuestionMutation.isPending || updateQuestionMutation.isPending}
                  >
                    {editingQuestion ? 'Update Question' : 'Add Question'}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <Card>
              <CardBody>
                <div className="text-center py-16">
                  <Target className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                  <p className="text-neutral-500">
                    Select a persona from the left to manage their strategic questions
                  </p>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonaManager;
